import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { useCart } from '../context/CartContext';
import api from '../api';
import ZoomableImage from '../components/ZoomableImage';
import '../styles/product-detail.css';

const ProductDetail = () => {
  const { id, slug } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [prevProduct, setPrevProduct] = useState(null);
  const [nextProduct, setNextProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showFullGallery, setShowFullGallery] = useState(false);
  const { settings } = useSettings();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [addedToCartMessage, setAddedToCartMessage] = useState(false);
  const [newReview, setNewReview] = useState({ reviewerName: '', rating: 5, comment: '' });
  const [reviewSubmitStatus, setReviewSubmitStatus] = useState('idle');

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setReviewSubmitStatus('loading');
    try {
      await fetch('/api/reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: product.id,
          reviewerName: newReview.reviewerName,
          rating: newReview.rating,
          comment: newReview.comment
        })
      });
      setReviewSubmitStatus('success');
      setNewReview({ reviewerName: '', rating: 5, comment: '' });
    } catch (err) {
      console.error(err);
      setReviewSubmitStatus('idle');
      alert('Có lỗi xảy ra, vui lòng thử lại sau.');
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    setActiveImageIndex(0);
    setReviews([]);
    setPrevProduct(null);
    setNextProduct(null);
    setQuantity(1);
    setAddedToCartMessage(false);

    const fetchProduct = slug
      ? api.getProductBySlug(slug)
      : api.getProduct(id);

    let mounted = true;

    fetchProduct
      .then(data => {
        if (!mounted) return;
        setProduct(data);
        window.currentTrackTarget = { type: 'product', id: data.id };
        api.trackEvent('product', data.id, 'view');

        api.getReviews(data.id)
          .then(reviewData => {
            if (!mounted) return;
            const items = Array.isArray(reviewData?.reviews)
              ? reviewData.reviews
              : Array.isArray(reviewData)
                ? reviewData
                : [];
            setReviews(items);
          })
          .catch(() => setReviews([]));

        const categorySlug = data.category?.slug || data.categoryId;

        if (categorySlug) {
          api.getProducts({ category: categorySlug })
            .then(allProducts => {
              if (!mounted) return;
              const items = Array.isArray(allProducts)
                ? allProducts
                : (allProducts.data || []);
              setRelatedProducts(items.filter(p => p.id !== data.id).slice(0, 3));
              const currentIndex = items.findIndex(p => String(p.id) === String(data.id));
              if (currentIndex !== -1) {
                setPrevProduct(currentIndex > 0 ? items[currentIndex - 1] : null);
                setNextProduct(currentIndex < items.length - 1 ? items[currentIndex + 1] : null);
              } else if (items.length > 0) {
                setPrevProduct(null);
                setNextProduct(items[0]);
              }
            })
            .catch(() => setRelatedProducts([]));
        } else {
          setRelatedProducts([]);
          setPrevProduct(null);
          setNextProduct(null);
        }
      })
      .catch(err => {
        if (!mounted) return;
        console.error('Failed to load product:', err);
        setError('Không tìm thấy sản phẩm');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
      window.currentTrackTarget = null;
    };
  }, [id, slug]);

  useEffect(() => {
    if (product) {
      document.title = (product.metaTitle || product.name) + ` - ${settings.shop_name || ''}`;

      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', product.metaDescription || product.shortDescription || '');
      }
    }

    return () => {
      document.title = `${settings.shop_name || ''} - ${settings.slogan || ''}`;
    };
  }, [product, settings]);

  const formatPrice = (price) => {
    if (price == null || price === '' || Number(price) === 0) return 'Liên hệ';
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  const calculateDiscount = () => {
    if (!product?.originalPrice || product.price == null || product.price === '' || Number(product.price) === 0) return 0;
    return Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <i key={i} className={i < Math.round(rating) ? 'fas fa-star' : 'far fa-star'}></i>
    ));
  };

  const getImages = () => {
    if (!product) return [];
    if (product.images && product.images.length > 0) {
      return product.images.map(img => ({
        url: api.imageUrl(img.url),
        alt: img.altText || product.name
      }));
    }
    return [{ url: 'https://via.placeholder.com/800x800?text=No+Image', alt: product?.name || 'No image' }];
  };

  const zaloLink = settings.zalo_url || '#';
  const phoneNumber = settings.phone || '';

  const displayedRating = Number(product?.rating || 0);
  const displayedReviewCount = Number(product?.reviewCount || reviews.length || 0);

  if (loading) {
    return (
      <div className="pd-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#999' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2.5rem', marginBottom: '1rem', display: 'block' }}></i>
          Đang tải sản phẩm...
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="pd-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#999' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block', color: '#e74c3c' }}></i>
          <h2>{error || 'Không tìm thấy sản phẩm'}</h2>
          <Link to="/products" className="pd-btn-primary" style={{ marginTop: '1rem', display: 'inline-block' }}>
            <i className="fas fa-arrow-left"></i> Quay lại sản phẩm
          </Link>
        </div>
      </div>
    );
  }

  const images = getImages();
  const categoryName = product.category?.name || 'Khác';

  const features = Array.isArray(product?.features)
    ? product.features.filter(Boolean)
    : product.longDescription
      ? product.longDescription
        .split('\n')
        .filter(line => line.trim().startsWith('-') || line.trim().startsWith('•'))
        .map(line => line.replace(/^[-•]\s*/, '').trim())
        .filter(Boolean)
      : [];

  const occasions = product.occasions
    ? (typeof product.occasions === 'string'
      ? product.occasions.split(',').map(s => s.trim()).filter(Boolean)
      : product.occasions.filter(Boolean))
    : [];

  const longDesc = (product?.longDescription || '').trim();
  const shortDesc = (product?.shortDescription || '').trim();

  const fallbackDesc = features.length > 0
    ? features.map(item => `- ${item}`).join('\n')
    : '';

  const displayDescription = longDesc || shortDesc || fallbackDesc;

  const normalizedDesc = displayDescription
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

  const normalizedFeaturesText = features
    .join(' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

  const shouldShowFeatures =
    features.length > 0 &&
    normalizedFeaturesText &&
    !normalizedDesc.includes(normalizedFeaturesText);

  return (
    <div className="pd-page">
      <div className="pd-breadcrumb">
        <div className="pd-container">
          <nav className="pd-breadcrumb-nav">
            <Link to="/">Trang chủ</Link>
            <i className="fas fa-chevron-right"></i>
            <Link to="/products">Sản phẩm</Link>
            <i className="fas fa-chevron-right"></i>
            <Link to={product.category?.slug ? `/products?category=${product.category.slug}` : `/products`}>
              {categoryName}
            </Link>
            <i className="fas fa-chevron-right"></i>
            <span>{product.name}</span>
          </nav>
        </div>
      </div>

      <section className="pd-hero">
        <div className="pd-container">
          <div className="pd-hero-grid">
            <div className="pd-gallery">
              <div className="pd-gallery-main">
                <img
                  src={images[activeImageIndex]?.url}
                  alt={images[activeImageIndex]?.alt}
                  loading="eager"
                  onClick={() => setShowFullGallery(true)}
                />

                {product.badge && (
                  <span className="pd-badge pd-badge--hot">{product.badge}</span>
                )}
                {calculateDiscount() > 0 && (
                  <span className="pd-badge pd-badge--sale">-{calculateDiscount()}%</span>
                )}

                {images.length > 1 && (
                  <>
                    <button
                      className="pd-gallery-nav pd-gallery-nav--prev"
                      onClick={() => setActiveImageIndex((prev) =>
                        prev === 0 ? images.length - 1 : prev - 1
                      )}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <button
                      className="pd-gallery-nav pd-gallery-nav--next"
                      onClick={() => setActiveImageIndex((prev) =>
                        (prev + 1) % images.length
                      )}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </>
                )}

                <button
                  className="pd-gallery-zoom"
                  onClick={() => setShowFullGallery(true)}
                >
                  <i className="fas fa-search-plus"></i>
                </button>

                {prevProduct && (
                  <Link
                    to={prevProduct.slug ? `/san-pham/${prevProduct.slug}` : `/products/${prevProduct.id}`}
                    className="pd-product-nav-arrow pd-product-nav-arrow--prev"
                    title={prevProduct.name}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </Link>
                )}
                {nextProduct && (
                  <Link
                    to={nextProduct.slug ? `/san-pham/${nextProduct.slug}` : `/products/${nextProduct.id}`}
                    className="pd-product-nav-arrow pd-product-nav-arrow--next"
                    title={nextProduct.name}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </Link>
                )}
              </div>

              {images.length > 1 && (
                <div className="pd-thumbs">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      className={`pd-thumb ${activeImageIndex === index ? 'pd-thumb--active' : ''}`}
                      onClick={() => setActiveImageIndex(index)}
                    >
                      <img src={image.url} alt={image.alt} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="pd-info">
              <div className="pd-info-top">
                <Link to={product.category?.slug ? `/products?category=${product.category.slug}` : `/products`} className="pd-category">
                  {categoryName}
                </Link>
                {product.badge && (
                  <span className="pd-status-badge">
                    <i className="fas fa-fire"></i> {product.badge}
                  </span>
                )}
              </div>

              <h1 className="pd-title">{product.name}</h1>

              <div className="pd-rating">
                <div className="pd-stars">
                  {renderStars(displayedRating)}
                </div>
                <span className="pd-rating-text">
                  {displayedRating.toFixed(1)}/5
                </span>
                <span className="pd-rating-count">
                  ({displayedReviewCount} đánh giá)
                </span>
              </div>

              <div className="pd-price-block">
                <span className="pd-price">{formatPrice(product.price)}</span>
                {product.originalPrice && (
                  <span className="pd-price-old">{formatPrice(product.originalPrice)}</span>
                )}
                {calculateDiscount() > 0 && (
                  <span className="pd-price-save">
                    Tiết kiệm {formatPrice(product.originalPrice - product.price)} (-{calculateDiscount()}%)
                  </span>
                )}
              </div>

              <p className="pd-short-desc">{product.shortDescription}</p>

              {features.length > 0 && (
                <div className="pd-features">
                  <h3 className="pd-features-title">Đặc điểm nổi bật</h3>
                  <ul className="pd-features-list">
                    {features.map((feature, index) => (
                      <li key={index}>
                        <i className="fas fa-check"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {occasions.length > 0 && (
                <div className="pd-occasions">
                  <h3 className="pd-occasions-title">Phù hợp cho dịp</h3>
                  <div className="pd-occasion-tags">
                    {occasions.map((occasion, index) => (
                      <span key={index} className="pd-occasion-tag">{occasion}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="pd-divider"></div>

              <div className="pd-cart-action-section">
                <div className="pd-qty-wrapper">
                  <span className="pd-qty-label">Số lượng:</span>
                  <div className="pd-qty-selector">
                    <button 
                      className="pd-qty-btn"
                      onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                    >
                      <i className="fas fa-minus"></i>
                    </button>
                    <span className="pd-qty-value">{quantity}</span>
                    <button 
                      className="pd-qty-btn"
                      onClick={() => setQuantity(prev => prev + 1)}
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                </div>

                <button 
                  className="pd-add-to-cart-btn"
                  onClick={() => {
                    addToCart(product, quantity);
                    setAddedToCartMessage(true);
                    setTimeout(() => setAddedToCartMessage(false), 3000);
                  }}
                >
                  <i className="fas fa-shopping-cart"></i> Thêm vào giỏ hàng
                </button>
                
                {addedToCartMessage && (
                  <div className="pd-added-msg">
                    <i className="fas fa-check-circle"></i> Đã thêm vào giỏ hàng thành công!
                  </div>
                )}
              </div>

              <div className="pd-divider"></div>

              <div className="pd-cta-section">
                <h3 className="pd-cta-title">
                  <i className="fas fa-headset"></i>
                  Liên hệ tư vấn & đặt hàng
                </h3>
                <p className="pd-cta-subtitle">
                  Gọi điện hoặc nhắn tin Zalo để được tư vấn và đặt hoa nhanh nhất
                </p>

                <div className="pd-cta-buttons">
                  <a
                    href={zaloLink}
                    className="pd-cta-btn pd-cta-btn--zalo"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      api.trackEvent('product', product.id, 'zalo');
                      if (window.gtag_report_conversion) window.gtag_report_conversion(zaloLink);
                    }}
                  >
                    <div className="pd-cta-btn-icon">
                      <img src="/images/zalo.svg" alt="Zalo" style={{ width: '32px', height: '32px' }} />
                    </div>
                    <div className="pd-cta-btn-text">
                      <strong>Chat Zalo</strong>
                      <small>Tư vấn miễn phí</small>
                    </div>
                  </a>

                  <a
                    href={`tel:${phoneNumber}`}
                    className="pd-cta-btn pd-cta-btn--phone"
                    onClick={() => {
                      api.trackEvent('product', product.id, 'phone');
                      if (window.gtag_report_conversion) window.gtag_report_conversion(`tel:${phoneNumber}`);
                    }}
                  >
                    <div className="pd-cta-btn-icon">
                      <i className="fas fa-phone-alt"></i>
                    </div>
                    <div className="pd-cta-btn-text">
                      <strong>Gọi ngay</strong>
                      <small>{phoneNumber}</small>
                    </div>
                  </a>
                </div>


              </div>

              <div className="pd-trust">
                <div className="pd-trust-item">
                  <div className="pd-trust-icon">
                    <i className="fas fa-shipping-fast"></i>
                  </div>
                  <div>
                    <strong>Giao hàng nhanh</strong>
                    <span>60 phút nội thành</span>
                  </div>
                </div>
                <div className="pd-trust-item">
                  <div className="pd-trust-icon">
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <div>
                    <strong>Đảm bảo chất lượng</strong>
                    <span>100% hoa tươi</span>
                  </div>
                </div>
                <div className="pd-trust-item">
                  <div className="pd-trust-icon">
                    <i className="fas fa-camera"></i>
                  </div>
                  <div>
                    <strong>Gửi ảnh trước khi giao</strong>
                    <span>Kiểm tra ảnh thực tế</span>
                  </div>
                </div>
                <div className="pd-trust-item">
                  <div className="pd-trust-icon">
                    <i className="fas fa-headset"></i>
                  </div>
                  <div>
                    <strong>Hỗ trợ 24/7</strong>
                    <span>Phản hồi tức thời</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pd-details">
        <div className="pd-container">
          <div className="pd-details-layout" style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
            
            {/* Description Section */}
            <div className="pd-tabs">
              <div className="pd-tabs-content">
                <div className="pd-panel">
                  <h3>Mô tả sản phẩm</h3>

                  <div className="pd-panel-desc" style={{ whiteSpace: 'pre-line' }}>
                    {displayDescription || 'Sản phẩm đang được cập nhật mô tả.'}
                  </div>

                  {shouldShowFeatures && (
                    <div className="pd-panel-features">
                      <h4>Đặc điểm nổi bật</h4>
                      <div className="pd-panel-features-grid">
                        {features.map((feature, index) => (
                          <div key={index} className="pd-panel-feature-item">
                            <i className="fas fa-check-circle"></i>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {occasions.length > 0 && (
                    <div className="pd-panel-occasions">
                      <h4>Phù hợp cho dịp</h4>
                      <div className="pd-panel-occasion-tags">
                        {occasions.map((occasion, index) => (
                          <span key={index} className="pd-panel-occasion-tag">{occasion}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Reviews Section */}
            <div className="pd-tabs">
              <div className="pd-tabs-content">
                <div className="pd-panel">
                  <h3>Đánh giá ({displayedReviewCount})</h3>

                  <div className="pd-reviews-summary">
                    <div className="pd-reviews-score">
                      <span className="pd-reviews-number">{displayedRating.toFixed(1)}</span>
                      <div className="pd-reviews-stars">
                        {renderStars(displayedRating)}
                      </div>
                      <span className="pd-reviews-count">{displayedReviewCount} đánh giá</span>
                    </div>

                    <div className="pd-reviews-bars">
                      {[5, 4, 3, 2, 1].map(star => {
                        const count = reviews.filter(r => Number(r.rating) === star).length;
                        const pct = reviews.length > 0 ? (count / reviews.length * 100) : 0;
                        return (
                          <div key={star} className="pd-review-bar">
                            <span>{star} <i className="fas fa-star"></i></span>
                            <div className="pd-review-bar-track">
                              <div
                                className="pd-review-bar-fill"
                                style={{ width: `${pct}%` }}
                              ></div>
                            </div>
                            <span className="pd-review-bar-count">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="pd-reviews-list">
                    <div className="pd-review-form-container" style={{ marginBottom: '2rem', padding: '1.5rem', background: '#faf9f7', borderRadius: '12px' }}>
                      <h4 style={{ marginTop: 0 }}>Viết đánh giá của bạn</h4>
                      {reviewSubmitStatus === 'success' ? (
                        <div style={{ color: '#2E7D32', padding: '1rem', background: '#e8f5e9', borderRadius: '8px' }}>
                          Cảm ơn bạn! Đánh giá đã được gửi và đang chờ duyệt.
                        </div>
                      ) : (
                        <form onSubmit={handleReviewSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                          <div>
                            <input 
                              type="text" 
                              placeholder="Tên của bạn" 
                              required 
                              value={newReview.reviewerName}
                              onChange={(e) => setNewReview({...newReview, reviewerName: e.target.value})}
                              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd' }}
                            />
                          </div>
                          <div>
                            <select 
                              value={newReview.rating}
                              onChange={(e) => setNewReview({...newReview, rating: Number(e.target.value)})}
                              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd' }}
                            >
                              <option value="5">5 Sao (Rất tốt)</option>
                              <option value="4">4 Sao (Tốt)</option>
                              <option value="3">3 Sao (Bình thường)</option>
                              <option value="2">2 Sao (Kém)</option>
                              <option value="1">1 Sao (Rất kém)</option>
                            </select>
                          </div>
                          <div>
                            <textarea 
                              placeholder="Đánh giá của bạn về sản phẩm này..." 
                              required 
                              rows="4"
                              value={newReview.comment}
                              onChange={(e) => setNewReview({...newReview, comment: e.target.value})}
                              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd', resize: 'vertical' }}
                            ></textarea>
                          </div>
                          <button 
                            type="submit" 
                            disabled={reviewSubmitStatus === 'loading'}
                            style={{ padding: '0.75rem 1.5rem', background: '#2E7D32', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', alignSelf: 'flex-start' }}
                          >
                            {reviewSubmitStatus === 'loading' ? 'Đang gửi...' : 'Gửi đánh giá'}
                          </button>
                        </form>
                      )}
                    </div>

                    {reviews.length === 0 && (
                      <p style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>
                        Chưa có đánh giá nào cho sản phẩm này.
                      </p>
                    )}

                    {reviews.map(review => (
                      <div key={review.id} className="pd-review-card">
                        <div className="pd-review-header">
                          <div className="pd-review-avatar">
                            {review.reviewerName ? review.reviewerName.charAt(0).toUpperCase() : '?'}
                          </div>
                          <div className="pd-review-meta">
                            <h4>{review.reviewerName || 'Khách hàng'}</h4>
                            <div className="pd-review-info">
                              <div className="pd-review-stars">{renderStars(review.rating)}</div>
                            </div>
                          </div>
                        </div>
                        <p className="pd-review-text">{review.comment || ''}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {relatedProducts.length > 0 && (
        <section className="pd-related">
          <div className="pd-container">
            <div className="pd-section-header">
              <h2>Sản phẩm tương tự</h2>
              <p>Có thể bạn quan tâm trong danh mục <strong>{categoryName}</strong></p>
            </div>

            <div className="pd-related-grid">
              {relatedProducts.map(rp => {
                const rpImg = rp.images && rp.images.length > 0
                  ? api.imageUrl(rp.images[0].url)
                  : 'https://via.placeholder.com/400';
                const rpAlt = rp.images?.[0]?.altText || rp.name;

                return (
                  <Link key={rp.id} to={rp.slug ? `/san-pham/${rp.slug}` : `/products/${rp.id}`} className="pd-related-card">
                    <div className="pd-related-img">
                      <img src={rpImg} alt={rpAlt} loading="lazy" />
                      <div className="pd-related-overlay">
                        <span>Xem chi tiết <i className="fas fa-arrow-right"></i></span>
                      </div>
                    </div>
                    <div className="pd-related-body">
                      <h3>{rp.name}</h3>
                      <div className="pd-related-stars">
                        {renderStars(rp.rating || 0)}
                        <span>({rp.reviewCount || 0})</span>
                      </div>
                      <div className="pd-related-price">
                        <span className="pd-price">{formatPrice(rp.price)}</span>
                        {rp.originalPrice && (
                          <span className="pd-price-old">{formatPrice(rp.originalPrice)}</span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="pd-related-actions">
              <Link to="/products" className="pd-btn-primary">
                <i className="fas fa-th-large"></i> Xem tất cả sản phẩm
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* 
      <div className="pd-floating-bar">
        <a
          href={zaloLink}
          className="pd-float-btn pd-float-btn--zalo"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            api.trackEvent('product', product.id, 'zalo');
            if (window.gtag_report_conversion) window.gtag_report_conversion(zaloLink);
          }}
        >
          <img src="/images/zalo.svg" alt="Zalo" style={{ width: '24px', height: '24px', marginRight: '8px' }} />
          <span>Chat Zalo</span>
        </a>
        <a
          href={`tel:${phoneNumber}`}
          className="pd-float-btn pd-float-btn--phone"
          onClick={() => {
            api.trackEvent('product', product.id, 'phone');
            if (window.gtag_report_conversion) window.gtag_report_conversion(`tel:${phoneNumber}`);
          }}
        >
          <i className="fas fa-phone-alt"></i>
          <span>Gọi ngay</span>
        </a>
      </div>
      */}

      {showFullGallery && (
        <div className="pd-modal" onClick={() => setShowFullGallery(false)}>
          <div className="pd-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="pd-modal-close" onClick={() => setShowFullGallery(false)}>
              <i className="fas fa-times"></i>
            </button>
            <div className="pd-modal-img">
              <ZoomableImage
                src={images[activeImageIndex]?.url}
                alt={images[activeImageIndex]?.alt}
                style={{ maxHeight: '85vh', objectFit: 'contain', width: '100%' }}
              />
            </div>
            {images.length > 1 && (
              <div className="pd-modal-nav">
                <button onClick={() => setActiveImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1)}>
                  <i className="fas fa-chevron-left"></i>
                </button>
                <span>{activeImageIndex + 1} / {images.length}</span>
                <button onClick={() => setActiveImageIndex(prev => (prev + 1) % images.length)}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;